import { useEffect } from 'react'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: 'website' | 'article' | 'product' | 'store'
  structuredData?: object
  noindex?: boolean
  canonical?: string
}

const SEO = ({
  title = 'ColmaFácil - Marketplace de Colmados en República Dominicana | Delivery Rápido',
  description = 'ColmaFácil es el mejor marketplace de colmados en República Dominicana. Compra productos frescos, alimentos, bebidas y más con delivery rápido. Conecta con los mejores colmados de RD y disfruta de precios justos y servicio de calidad.',
  keywords = 'colmados república dominicana, delivery colmados, marketplace colmados, comprar productos frescos RD, delivery rápido dominicana, colmados online, supermercado online RD, comprar alimentos online, delivery comida RD, tienda online dominicana, colmados cerca de mí, productos frescos delivery, comprar en colmados, marketplace RD, app colmados dominicana',
  image = 'https://colmafacil.store/image/ColmaFacil-Logo.png',
  url = typeof window !== 'undefined' ? window.location.href : 'https://colmafacil.store',
  type = 'website',
  structuredData,
  noindex = false,
  canonical
}: SEOProps) => {
  useEffect(() => {
    const baseUrl = 'https://colmafacil.store'
    const fullTitle = title.includes('ColmaFácil') ? title : `${title} | ColmaFácil`
    const canonicalUrl = canonical || url

    // Actualizar título
    document.title = fullTitle

    // Función helper para actualizar o crear meta tags
    const updateMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name'
      let meta = document.querySelector(`meta[${attribute}="${property}"]`) as HTMLMetaElement
      
      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute(attribute, property)
        document.head.appendChild(meta)
      }
      meta.content = content
    }

    // Función helper para actualizar o crear link tags
    const updateLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement
      
      if (!link) {
        link = document.createElement('link')
        link.rel = rel
        document.head.appendChild(link)
      }
      link.href = href
    }

    // Primary Meta Tags
    updateMetaTag('title', fullTitle)
    updateMetaTag('description', description)
    updateMetaTag('keywords', keywords)
    
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow')
    } else {
      updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1')
    }

    // Canonical URL
    updateLinkTag('canonical', canonicalUrl)

    // Open Graph / Facebook
    updateMetaTag('og:type', type, true)
    updateMetaTag('og:url', url, true)
    updateMetaTag('og:title', fullTitle, true)
    updateMetaTag('og:description', description, true)
    updateMetaTag('og:image', image, true)
    updateMetaTag('og:image:width', '1200', true)
    updateMetaTag('og:image:height', '630', true)
    updateMetaTag('og:image:alt', fullTitle, true)
    updateMetaTag('og:site_name', 'ColmaFácil', true)
    updateMetaTag('og:locale', 'es_DO', true)
    updateMetaTag('og:locale:alternate', 'es_ES', true)

    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image')
    updateMetaTag('twitter:url', url)
    updateMetaTag('twitter:title', fullTitle)
    updateMetaTag('twitter:description', description)
    updateMetaTag('twitter:image', image)
    updateMetaTag('twitter:image:alt', fullTitle)
    updateMetaTag('twitter:creator', '@ColmaFacil')
    updateMetaTag('twitter:site', '@ColmaFacil')

    // Structured Data
    if (structuredData) {
      // Eliminar structured data anterior si existe
      const existingScript = document.querySelector('script[type="application/ld+json"][data-seo-dynamic]')
      if (existingScript) {
        existingScript.remove()
      }

      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.setAttribute('data-seo-dynamic', 'true')
      script.textContent = JSON.stringify(structuredData)
      document.head.appendChild(script)
    }
  }, [title, description, keywords, image, url, type, structuredData, noindex, canonical])

  return null
}

export default SEO

